import { cn } from "@/utils/theme";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { Icon } from "@elaniintc/goes-design-system";
import { VariableKeyBox } from "../key-box";
import { useGetServiceEndpoints } from "@/queries/interoperability/interoperability.api";
import type { ParameterDefinition } from "@/workflow/components/configurations/interoperability/types";
import { useEffect, useMemo, useRef } from "react";
import { useGetInteroperabilityNodeData } from "@/queries/flows/flows.api";
import type { InteroperabilityConfiguration } from "@/workflow/components/configurations/interoperability/types";
import { deserializeInteroperabilityNode } from "@/workflow/components/configurations/interoperability/serialization/deserialization";
import type { NodeData } from "@/workflow_api/types/workflow";

interface Props {
  id: string;
}

const Content = ({ nodeId, flowId }: { nodeId: string; flowId: string }) => {
  const {
    mutate: getNodeData,
    data: apiNode,
    isPending: isLoadingNodeData,
    isError,
    error,
  } = useGetInteroperabilityNodeData();

  const hasFetched = useRef(false);

  useEffect(() => {
    if (flowId && nodeId && !hasFetched.current) {
      getNodeData({ flowId, nodeId });
      hasFetched.current = true;
    }
  }, [flowId, nodeId, getNodeData]);

  // Convert ApiNode to NodeData format for deserialization
  const nodeData: NodeData | null = useMemo(() => {
    if (!apiNode) return null;

    return {
      type: apiNode.ref_name,
      node_type: apiNode.type,
      displayName: apiNode.display_name,
      inputs: apiNode.inputs || [],
      order: apiNode.order,
      edge: apiNode.edge
        ? {
            source: apiNode.edge.source || "",
            source_handler: apiNode.edge.source_handler || "",
            target_handler: apiNode.edge.target_handler || "",
          }
        : null,
      connections: apiNode.connections,
    };
  }, [apiNode]);

  // Deserialize configuration from node inputs
  // Note: We pass undefined for nodeVariables since we only need serviceId and endpointId
  // and don't need to resolve variable names for displaying output variables
  const config: InteroperabilityConfiguration | null = useMemo(() => {
    if (!nodeData) return null;
    return deserializeInteroperabilityNode(nodeData, undefined);
  }, [nodeData]);

  // Get the endpoints for the selected service
  const { data: endpoints, isLoading: isLoadingEndpoints } =
    useGetServiceEndpoints(config?.serviceId || "");

  // Find the selected endpoint data
  const selectedEndpointData = useMemo(() => {
    if (!endpoints || !config?.endpointId) return null;
    return endpoints.find(
      (e: { id: string; service: unknown }) => e.id === config.endpointId,
    );
  }, [endpoints, config?.endpointId]);

  // Extract output variables from response 200 data
  const outputVariables = useMemo(() => {
    if (!selectedEndpointData?.service) return [];

    const service = selectedEndpointData.service as {
      responses?: {
        200?: {
          data?: Record<string, ParameterDefinition>;
        };
      };
    };

    const responseData = service.responses?.["200"]?.data;
    if (!responseData) return [];

    return Object.entries(responseData).map(([key, value]) => {
      // Normalize type to match VariableKeyBox expectations
      let normalizedType = value.type || "string";
      // Map common type variations to standard types
      if (normalizedType === "text") {
        normalizedType = "string";
      }
      return {
        name: value.display_name || key,
        type: normalizedType,
        description: value.description || "",
      };
    });
  }, [selectedEndpointData]);

  if (isError) {
    return (
      <div className="text-sm text-red-500">
        {error?.message || "An unexpected error occurred."}
      </div>
    );
  }

  if (isLoadingNodeData || isLoadingEndpoints) {
    return (
      <Icon
        name="spinner-third"
        className="animate-spin text-sm text-on-disabled"
        type="far"
      />
    );
  }

  if (!config?.endpointId || outputVariables.length === 0) {
    return null;
  }

  return (
    <div className="flex">
      <div
        className={cn(
          "w-0.5 relative translate-x-[25px]",
          "bg-neutral-object-accent",
        )}
      >
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <div className="space-y-3">
            {outputVariables.map((variable, index) => (
              <VariableKeyBox
                key={`variable-${variable.name}-${index}`}
                name={variable.name}
                type={variable.type}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const InteroperabilityVariables = ({ id }: Props) => {
  const { getNodeById, flowId } = useWorkflowStore();
  const node = getNodeById(id);

  if (!node) return <></>;

  return <Content nodeId={id} flowId={flowId} />;
};
